import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Activity, CheckCircle2, Clock, Truck, Edit2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { buildStatusTimeline, formatDateTime } from '../../../../../features/orderDetails/orderUtils';
import EditHistoryDetailModal from '../modals/EditHistoryDetailModal';

const OrderHistoryTab = ({ order }) => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.user_type?.includes('admin');

  const [editingEvent, setEditingEvent] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [error, setError] = useState('');

  const timeline = useMemo(() => buildStatusTimeline(order), [order]);

  /**
   * Icon for the milestone the event represents. Keyed by the stored status
   * rather than by its label, so it keeps working if a label is reworded.
   */
  const getStatusIcon = (status) => {
    if (status === 'delivered' || status === 'withdrawn') return <CheckCircle2 size={16} />;
    if (['in_transit', 'on_the_way', 'in_cd', 'dropped_at_hub', 'received_at_hub'].includes(status)) {
      return <Truck size={16} />;
    }
    if (['pending', 'created_on_courier', 'in_preparation'].includes(status)) {
      return <Clock size={16} />;
    }
    return <Activity size={16} />;
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger px-5 py-3 rounded-2xl border border-red-200 dark:border-red-900 text-sm font-medium flex items-center justify-between gap-4">
          <span>{error}</span>
          <button onClick={() => setError('')} className="font-bold shrink-0">Cerrar</button>
        </div>
      )}

      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden p-8">
        <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-8 flex items-center gap-2">
          <Activity className="text-ninja-green" size={20} /> Historial de Estados
        </h3>

        {timeline.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-ninja-gray dark:bg-[#2d2d2d] rounded-full flex items-center justify-center mb-4">
              <Activity size={24} className="text-text-muted" />
            </div>
            <h4 className="text-lg font-bold text-ninja-dark dark:text-white mb-2">Sin movimientos</h4>
            <p className="text-text-secondary max-w-sm">
              Esta orden todavía no registra cambios de estado.
            </p>
          </div>
        ) : (
          <div className="relative pl-6 border-l-2 border-border space-y-8">
            {timeline.map((event) => (
              <div key={`${event.id}-${event.date}`} className="relative">
                <div className="absolute -left-[35px] top-0 w-8 h-8 rounded-full bg-white dark:bg-[#1a1a1a] border-2 border-ninja-green text-ninja-green flex items-center justify-center">
                  {getStatusIcon(event.status)}
                </div>

                <h4 className="text-sm font-bold text-ninja-dark dark:text-white uppercase tracking-wide flex items-center gap-2">
                  {event.statusLabel}
                  {event.substatusLabel && (
                    <span className="text-text-secondary normal-case font-medium">
                      : {event.substatusLabel}
                    </span>
                  )}
                  {isAdmin && (
                    <Button
                      variant="subtle"
                      size="sm"
                      icon={Edit2}
                      iconOnly
                      onClick={() => setEditingEvent(event)}
                      aria-label="Editar detalle del estado"
                      className="bg-ninja-gray/50 hover:!text-ninja-green hover:!bg-ninja-greenLight"
                    />
                  )}
                </h4>

                <p className="text-text-secondary text-sm mt-1">
                  {event.details ? `Detalles entrega: ${event.details}` : 'Actualización de estado'}
                </p>

                <p className="text-xs font-semibold text-text-muted mt-2">
                  {formatDateTime(event.date)}
                </p>

                {event.imageUrls.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {event.imageUrls.map((url) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setPreviewImage(url)}
                        className="w-12 h-12 rounded-lg overflow-hidden border border-border hover:border-ninja-green transition-colors"
                      >
                        <img src={url} alt="Evidencia de entrega" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Provenance of the event: only useful to the internal team. */}
                {isAdmin && (event.source || event.externalSource) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {event.source && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-ninja-gray dark:bg-[#2d2d2d] text-text-muted">
                        Source: {event.source}
                      </span>
                    )}
                    {event.externalSource && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-ninja-gray dark:bg-[#2d2d2d] text-text-muted">
                        External: {event.externalSource}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <EditHistoryDetailModal
        event={editingEvent}
        onClose={() => setEditingEvent(null)}
        order={order}
        onError={setError}
      />

      {previewImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Evidencia de entrega"
            className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

export default OrderHistoryTab;
